package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import br.gov.go.pge.permanenciaweb.shared.CountsDto
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import br.gov.go.pge.permanenciaweb.shared.SnapshotDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontVariantNumeric
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontVariant
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

sealed interface KpiValueKind : ComponentKind

sealed interface KpiCardKind : ComponentKind

val KpiCardStyle = CssStyle<KpiCardKind> {
    base {
        Modifier
            .backgroundColor(IndustrialPalette.White)
            // 1px hairline borders stay in px per the units policy.
            .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
            .borderRadius(0.375.cssRem)
            .padding(0.75.cssRem, 1.cssRem)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.25.cssRem)
    }
}

/** Card whose filter is on: uniform steel tone, so every active card reads the same. */
val KpiCardActiveVariant = KpiCardStyle.addVariantBase {
    Modifier
        .backgroundColor(IndustrialPalette.SteelTint)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Steel)
}

val KpiLabelStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Muted)
        .fontSize(0.75.cssRem)
        .textTransform(TextTransform.Uppercase)
        .letterSpacing(0.04.em)
}

val KpiValueStyle = CssStyle<KpiValueKind> {
    base {
        Modifier
            .fontSize(1.75.cssRem)
            .fontWeight(FontWeight.Bold)
            .fontVariant { numeric(FontVariantNumeric.TabularNums) }
    }
}

val KpiValueSteelVariant = KpiValueStyle.addVariantBase { Modifier.color(IndustrialPalette.Steel) }
val KpiValueOrangeVariant = KpiValueStyle.addVariantBase { Modifier.color(IndustrialPalette.Orange) }
val KpiValueRedVariant = KpiValueStyle.addVariantBase { Modifier.color(IndustrialPalette.Red) }

/**
 * KPI strip. Cards declared in [KPI_CARDS] with a quick filter become toggle buttons whose number
 * follows the filtered roster; the others show the global snapshot count (Power BI `ALL()`).
 *
 * [rows] is the roster after every active filter, which is what gives the clickable cards their
 * Power BI "visual filter" numbers.
 */
@Composable
internal fun KpiStrip(snapshot: SnapshotDto?, state: DashboardState, rows: List<OccupantDto>) {
    val counts = snapshot?.counts
    val quickCounts = remember(rows) { rows.quickFilterCounts() }

    SimpleGrid(
        // Seven cards no longer fit as a single row on narrow screens; keep the desktop row
        // and let smaller breakpoints wrap into fewer columns.
        numColumns(2, sm = 3, md = 4, lg = KPI_CARDS.size),
        modifier = Modifier
            .gap(0.75.cssRem)
            .padding(top = 0.cssRem, right = 1.5.cssRem, bottom = 0.75.cssRem, left = 1.5.cssRem),
    ) {
        for (card in KPI_CARDS) {
            val id = card.quickFilterId
            if (id == null) {
                KpiCardBody(
                    card = card,
                    value = counts?.let { snapshotCounts -> card.globalCount?.invoke(snapshotCounts) },
                    active = false,
                    filterId = null,
                    onClick = {},
                )
            } else {
                KpiCardBody(
                    card = card,
                    value = if (counts == null) null else quickCounts[id] ?: 0,
                    active = id in state.quickFilters,
                    filterId = id,
                    onClick = { state.toggleQuickFilter(id) },
                )
            }
        }
    }
}

@Composable
private fun KpiCardBody(
    card: KpiCard,
    value: Int?,
    active: Boolean,
    filterId: QuickFilterId?,
    onClick: () -> Unit,
) {
    val modifier = KpiCardStyle.toModifier(KpiCardActiveVariant.takeIf { active })

    val content: @Composable () -> Unit = {
        Span(attrs = KpiLabelStyle.toAttrs()) { Text(card.label) }
        Span(attrs = KpiValueStyle.toModifier(card.valueVariant).toAttrs()) {
            Text(value?.toString() ?: "–")
        }
    }

    if (filterId == null) {
        Div(attrs = modifier.toAttrs { attr("data-kpi-card", card.id) }) { content() }
    } else {
        HtmlButton(
            attrs = modifier
                .then(
                    Modifier
                        .width(100.percent)
                        .cursor(Cursor.Pointer)
                        // Raw <button>: keep the card typography and left alignment.
                        .styleModifier {
                            property("font", "inherit")
                            property("text-align", "start")
                        },
                )
                .toAttrs {
                    attr("type", "button")
                    attr("data-kpi-card", card.id)
                    attr("data-quick-filter", card.id)
                    attr("aria-pressed", active.toString())
                    onClick { onClick() }
                },
        ) {
            content()
        }
    }
}
